"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Loader2 } from "lucide-react"
import {
  listCreditPackCatalog,
  startCreditPackCheckout,
  getCreditPackBalances,
  getCurrentSubscription,
} from "@/src/utils/billingApi"
import { useSession } from "@/app/context/AuthContext"
import { useToast } from "@/hooks/use-toast"
import { extractPrice, hasActiveSubscription, getPricingBadge, formatCurrency } from "@/src/utils/pricing-helpers"

interface CreditPack {
  id: string
  credits: number
  price: number
  pricePerCredit: number
  savings: string | null
  bestValue?: boolean
  badge?: string | null
}

export function CreditPacksSection() {
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([])
  const [loading, setLoading] = useState(true)
  const [purchaseLoading, setPurchaseLoading] = useState<string | null>(null)
  const [successHandled, setSuccessHandled] = useState(false)
  const [hasSubscription, setHasSubscription] = useState(false)
  const { data: session, status } = useSession()
  const { toast } = useToast()
  const router = useRouter()

  useEffect(() => {
    const checkSubscription = async () => {
      if (session?.user?.id) {
        console.log("[v0] CreditPacksSection - Checking subscription status for user:", session.user.id)
        try {
          const result = await getCurrentSubscription(session.user.id)
          if (result.success && result.subscription) {
            const qualifies = hasActiveSubscription(result.subscription)
            setHasSubscription(qualifies)
            console.log("[v0] CreditPacksSection - User subscription status:", {
              planKey: result.subscription.planKey,
              status: result.subscription.status,
              qualifiesForMemberPricing: qualifies,
            })
          }
        } catch (error) {
          console.error("[v0] CreditPacksSection - Error checking subscription:", error)
        }
      } else {
        console.log("[v0] CreditPacksSection - No user session, treating as non-subscriber")
      }
    }

    if (status !== "loading") {
      checkSubscription()
    }
  }, [session?.user?.id, status])

  useEffect(() => {
    if (successHandled) return

    const urlParams = new URLSearchParams(window.location.search)
    const checkoutParam = urlParams.get("checkout")

    const cleanCheckoutParam = checkoutParam?.includes("success") ? "success" : checkoutParam

    console.log("[v0] CreditPacksSection - URL params:", {
      checkout: checkoutParam,
      cleanCheckout: cleanCheckoutParam,
      type: urlParams.get("type"),
      credits: urlParams.get("credits"),
      allParams: Object.fromEntries(urlParams.entries()),
    })

    if (cleanCheckoutParam === "mock" && urlParams.get("type") === "credits") {
      const credits = urlParams.get("credits") || "100"
      console.log("[v0] CreditPacksSection - Mock mode redirect with credits:", credits)
      setSuccessHandled(true)
      router.push(`/confirmation/thank-you?credits=${credits}`)
      return
    }

    if (cleanCheckoutParam === "success" && status !== "loading") {
      const checkoutType = sessionStorage.getItem("checkout_type")
      console.log("[v0] CreditPacksSection - Checkout type from session storage:", checkoutType)
      console.log("[v0] CreditPacksSection - Session status:", status)
      console.log("[v0] CreditPacksSection - User ID:", session?.user?.id)

      if (checkoutType !== "credit_pack") {
        console.log("[v0] CreditPacksSection - Not a credit pack checkout, skipping")
        return
      }

      console.log("[v0] CreditPacksSection - Processing credit pack success...")
      setSuccessHandled(true)

      const handleCreditPackSuccess = async () => {
        console.log("[v0] CreditPacksSection - Live mode credit pack success detected")

        const storedCreditAmount = sessionStorage.getItem("credit_amount")
        const creditAmount = storedCreditAmount ? Number.parseInt(storedCreditAmount) : 50
        console.log("[v0] CreditPacksSection - Using stored credit amount:", creditAmount)

        sessionStorage.removeItem("checkout_type")
        sessionStorage.removeItem("credit_amount")

        try {
          const userId = session?.user?.id
          if (!userId) {
            console.log("[v0] CreditPacksSection - No user ID available, using fallback redirect")
            router.push(`/confirmation/thank-you?credits=${creditAmount}`)
            return
          }

          const balanceData = await getCreditPackBalances(userId)
          console.log("[v0] CreditPacksSection - Credit pack balance data:", balanceData)

          if (balanceData.success) {
            console.log("[v0] CreditPacksSection - Redirecting with credits:", creditAmount)
            router.push(`/confirmation/thank-you?credits=${creditAmount}`)
          } else {
            console.log("[v0] CreditPacksSection - API call failed, using fallback redirect")
            router.push(`/confirmation/thank-you?credits=${creditAmount}`)
          }
        } catch (error) {
          console.error("[v0] CreditPacksSection - Error fetching credit pack data:", error)
          router.push(`/confirmation/thank-you?credits=${creditAmount}`)
        }
      }

      handleCreditPackSuccess()
    }
  }, [router, session?.user?.id, status, successHandled])

  useEffect(() => {
    const loadCreditPacks = async () => {
      try {
        setLoading(true)

        const mode = !session ? "nosub" : "auto"
        const userId = session?.user?.id

        console.log("[v0] CreditPacksSection - Loading catalog with mode:", { mode, userId: !!userId })

        const result = await listCreditPackCatalog({ mode, userId })

        if (result.success && result.catalog) {
          console.log("[v0] CreditPacksSection - Catalog loaded:", {
            itemCount: result.catalog.length,
            requiresSubscription: result.requiresSubscription,
          })

          const dynamicPacks: CreditPack[] = result.catalog.map((pack) => {
            const price = extractPrice(pack)
            const pricePerCredit = price > 0 ? price / pack.creditUnits : 0
            const badge = getPricingBadge(pack)

            return {
              id: pack.packKey,
              credits: pack.creditUnits,
              price: price,
              pricePerCredit: pricePerCredit,
              savings: null,
              bestValue: false,
              badge: badge,
            }
          })

          if (dynamicPacks.length > 0) {
            const bestValuePack = dynamicPacks.reduce((best, current) =>
                current.pricePerCredit > 0 && current.pricePerCredit < best.pricePerCredit ? current : best,
            )
            bestValuePack.bestValue = true
          }

          setCreditPacks(dynamicPacks)
        } else {
          throw new Error("Failed to load credit pack catalog")
        }
      } catch (error) {
        console.error("Failed to load credit packs:", error)
        toast({
          title: "Error loading credit packs",
          description: "Failed to load credit pack options. Please refresh the page.",
          variant: "destructive",
        })
      } finally {
        setLoading(false)
      }
    }

    loadCreditPacks()
  }, [toast, session])

  const handleCreditPackClick = async (packKey: string) => {
    if (!session?.user?.id) {
      toast({
        title: "Authentication required",
        description: "Please log in to purchase credit packs.",
        variant: "destructive",
      })
      window.location.href = "/login?redirect=/pricing"
      return
    }

    try {
      setPurchaseLoading(packKey)
      const pack = creditPacks.find((p) => p.id === packKey)
      const creditAmount = pack ? pack.credits : 100

      sessionStorage.setItem("checkout_type", "credit_pack")
      sessionStorage.setItem("credit_amount", creditAmount.toString())

      await startCreditPackCheckout(packKey, 1, session.user.id, creditAmount)
      setTimeout(() => {
        if (purchaseLoading === packKey) {
          toast({
            title: "Checkout delayed",
            description: "If you weren't redirected to checkout, please try again.",
            variant: "destructive",
          })
          setPurchaseLoading(null)
        }
      }, 3000)
    } catch (error) {
      console.error("Credit pack checkout error:", error)
      toast({
        title: "Checkout failed",
        description: "Unable to start checkout process. Please try again.",
        variant: "destructive",
      })
      setPurchaseLoading(null)
    }
  }

  if (loading) {
    return (
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Credit Packs</h2>
            <p className="text-gray-600">Loading credit pack options...</p>
          </div>
        </div>
    )
  }

  if (creditPacks.length === 0) {
    return null
  }

  return (
      <div className="mb-16">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-4">Credit Packs</h2>
          <h3 className="text-xl font-semibold text-[#40A1AB] mb-2">
            Black Friday Promotional Pricing for a Limited Time!
          </h3>
          <p className="text-gray-600">Purchase credits as needed for one-time projects</p>
          {!session && <p className="text-sm text-muted-foreground mt-2">Member pricing available with subscription</p>}
        </div>

        <div
            className={
              creditPacks.length < 3 ? "flex flex-wrap justify-center gap-6" : "grid grid-cols-1 md:grid-cols-3 gap-6"
            }
        >
          {creditPacks.map((pack) => (
              <Card key={pack.id} className={`relative ${pack.bestValue ? "ring-2 ring-[#40A1AB]" : ""}`}>
                {pack.bestValue && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-[#40A1AB] text-white px-4 py-1">Best Value</Badge>
                    </div>
                )}

                <CardHeader className="text-center">
                  <CardTitle className="text-xl">{pack.credits} Credits</CardTitle>
                  <div className="mt-2">
                <span className="text-3xl font-bold">
                  {pack.price > 0 ? `$${formatCurrency(pack.price)}` : "Contact for pricing"}
                </span>
                    {pack.badge && (
                        <div className="mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {pack.badge}
                          </Badge>
                        </div>
                    )}
                  </div>
                  <CardDescription>
                    {pack.price > 0 ? `$${pack.pricePerCredit.toFixed(2)} per credit` : "Pricing available upon request"}
                  </CardDescription>
                  {session && !hasSubscription && (
                      <div className="mt-3 flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                        <span className="text-base">💡</span>
                        <span>Members save on credits</span>
                      </div>
                  )}
                </CardHeader>

                <CardFooter>
                  <Button
                      onClick={() => handleCreditPackClick(pack.id)}
                      className={`w-full ${pack.bestValue ? "bg-[#40A1AB] hover:bg-[#3A919A] text-white" : ""}`}
                      variant={pack.bestValue ? "default" : "outline"}
                      disabled={purchaseLoading === pack.id}
                  >
                    {purchaseLoading === pack.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                    ) : (
                        "Buy Now"
                    )}
                  </Button>
                </CardFooter>
              </Card>
          ))}
        </div>
      </div>
  )
}
